import { BUILD_SHA } from '@/lib/build-info'
import {
  type SupportSession,
  endSupportSession,
  isSupportSessionLive,
  keepLinksInThisTab,
  markSupportSessionEnded,
} from '@/lib/support-session'
import { cn } from '@/lib/utils'
import { EyeIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import * as Auth from '@aws-amplify/auth'

import { Button } from '@droidmap/frontend-ui'
import { HttpResponseError } from '@droidmap/shared-utils'
import type { User } from '@droidmap/user-service-contract'

import type { AuthResult, UseAuth } from './AuthContext.js'
import { useConfig } from './ConfigContext.js'

type SupportSessionState = {
  session: SupportSession
  /** Milliseconds left, refreshed every second. */
  remainingMs: number
  /** Set once the session expired or was ended; the app is covered then. */
  ended: boolean
  end: () => Promise<void>
}

const SupportSessionContext = createContext<SupportSessionState | undefined>(
  undefined,
)

/** The support session the app is running under, or `undefined` if none. */
export const useSupportSession = (): SupportSessionState | undefined =>
  useContext(SupportSessionContext)

/**
 * Whether the tab is running a support session, from a context of its own:
 * the session's context changes with the countdown every second, and a
 * component asking only this need not re-render with it.
 */
const InSupportSessionContext = createContext(false)

/** Whether the app is a staff member's read-only view of a customer. */
export const useIsInSupportSession = (): boolean =>
  useContext(InSupportSessionContext)

const unavailable = async (): Promise<AuthResult> => ({
  success: false,
  message: 'Unavailable in a read-only support session',
})

/**
 * The auth a support session presents to the app: already signed in as the
 * customer, with the session token as the bearer for every service call.
 * Every sign-in, sign-up and password flow is refused; signing out ends the
 * session. Amplify is never configured in a tab running this.
 */
const useSupportSessionAuth = (
  session: SupportSession,
  end: () => Promise<void>,
): UseAuth =>
  useMemo<UseAuth>(() => {
    const info = session.session
    const [firstName = '', ...rest] = info.targetName.split(' ')
    const userDetails = {
      id: info.targetUserId,
      userEmail: info.targetEmail,
      username: info.targetEmail,
      firstName,
      lastName: rest.join(' '),
    } as User
    return {
      user: { username: info.targetEmail } as Auth.AuthUser,
      username: info.targetEmail,
      userDetails,
      isLoading: false,
      isAuthenticated: true,
      signIn: unavailable,
      signOut: async () => {
        await end()
        return { success: true }
      },
      signUp: unavailable,
      autoSignIn: async () => {
        throw new Error('Unavailable in a read-only support session')
      },
      getAccessToken: async () => session.token,
      getIdToken: async () => session.token,
      getDroidmapId: async () => info.targetUserId,
      forgotPassword: unavailable,
      confirmPassword: unavailable,
      requestOTP: unavailable,
      verifyOTP: unavailable,
    }
  }, [session, end])

/**
 * Runs the app as a support session: the customer's identity with the
 * session token, a countdown, and a full-screen stop once the session ends.
 * `renderAuth` receives the auth value to provide, so this module does not
 * import the auth context it sits beside.
 */
export const SupportSessionProvider = ({
  session,
  renderAuth,
  now = Date.now,
}: {
  session: SupportSession
  renderAuth: (auth: UseAuth) => ReactNode
  now?: () => number
}) => {
  const { ENV } = useConfig()
  const expiresAt = new Date(session.session.expiresAt).getTime()
  const [remainingMs, setRemainingMs] = useState(() => expiresAt - now())
  // Already over when the tab loads it (a reload after expiry): covered from
  // the first render, so nothing calls a service with a spent token.
  const [ended, setEnded] = useState(() => expiresAt <= now())

  useEffect(() => {
    const tick = () => {
      const left = expiresAt - now()
      setRemainingMs(left)
      if (left <= 0) {
        setEnded(true)
      }
    }
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [expiresAt, now])

  // The session is this tab's alone: links opened in another would not be in it.
  useEffect(() => keepLinksInThisTab(document, window.location), [])

  // A service refusing the session means it is over, whatever the clock
  // says: ended from another tab, or refused once its time ran out.
  const queryClient = useQueryClient()
  useEffect(() => {
    return subscribeToCacheErrors(queryClient, (error) => {
      if (isSessionOverRefusal(error)) {
        setEnded(true)
      }
    })
  }, [queryClient])

  // And ask outright every 30 seconds, the same bound services work to: most
  // route loaders call services outside the query cache, so a refusal there
  // would not reach the listener above.
  // Not once it is over: nothing is left to ask about.
  useEffect(() => {
    if (ended) {
      return
    }
    let cancelled = false
    const timer = window.setInterval(() => {
      void isSupportSessionLive(session, ENV).then((live) => {
        if (!live && !cancelled) {
          setEnded(true)
        }
      })
    }, SESSION_STATUS_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [session, ENV, ended])

  const end = useCallback(async () => {
    setEnded(true)
    await endSupportSession(session, ENV).catch(() => undefined)
  }, [session, ENV])

  // In the provider, not the banner: every route, the top-level ones the
  // banner does not render on included, carries the mark in its tab title.
  useEffect(() => {
    const original = document.title
    if (!original.startsWith('[SUPPORT]')) {
      document.title = `[SUPPORT] ${original}`
    }
    return () => {
      document.title = original
    }
  }, [])

  const auth = useSupportSessionAuth(session, end)
  const state = useMemo(
    () => ({ session, remainingMs, ended, end }),
    [session, remainingMs, ended, end],
  )

  return (
    <InSupportSessionContext.Provider value={true}>
      <SupportSessionContext.Provider value={state}>
        {ended ? (
          <SessionEnded
            assist={session.session.mode === 'assist'}
            orgName={session.session.orgName}
          />
        ) : (
          renderAuth(auth)
        )}
      </SupportSessionContext.Provider>
    </InSupportSessionContext.Provider>
  )
}

/**
 * Replaces the app once a session is over, so nothing keeps calling services
 * with a token they will refuse. There is nothing to refresh: a new session
 * starts from the admin console.
 */
const SessionEnded = ({
  assist,
  orgName,
}: {
  /** An assist session ended, rather than a read-only one. */
  assist: boolean
  orgName?: string
}) => {
  useEffect(() => {
    // The token must not outlive the session in this tab, but the mark that
    // it ended does: a reload opens here again, not on a customer sign-in.
    markSupportSessionEnded()
  }, [])
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-6">
      <div className="max-w-md text-center space-y-3">
        {assist ? (
          <WrenchScrewdriverIcon
            className="h-10 w-10 mx-auto text-accent"
            aria-hidden
          />
        ) : (
          <EyeIcon className="h-10 w-10 mx-auto text-warning" aria-hidden />
        )}
        <h1 className="text-xl font-semibold">Support session ended</h1>
        <p className="text-sm text-text-dim">
          {assist
            ? `This read-write session in ${orgName ?? 'the organisation'} has expired or was ended. Start a new one from the admin console if there is more to do.`
            : 'This read-only session has expired or was ended. Start a new one from the admin console if you need to keep looking.'}
        </p>
        <Button variant="outline" onClick={() => window.close()}>
          Close this tab
        </Button>
      </div>
    </div>
  )
}

/**
 * Why a service refused a request in a support session: `read_only`, any
 * write in observe; `wrong_org`, an assist session's write outside its org
 * (or naming none), or an observe session's page outside the org its help
 * request was raised in; `not_allowed`, a change no support session may
 * make (creating an organisation or a token, say).
 */
export type SupportSessionRefusalKind =
  'read_only' | 'wrong_org' | 'not_allowed'

const REFUSAL_KINDS: Record<string, SupportSessionRefusalKind> = {
  support_session_read_only: 'read_only',
  support_session_wrong_org: 'wrong_org',
  support_session_not_allowed: 'not_allowed',
}

/** The kind of a service's support-session refusal, if `error` is one. */
export const refusalKindOf = (
  error: unknown,
): SupportSessionRefusalKind | undefined =>
  error instanceof HttpResponseError && error.serverMessage !== undefined
    ? REFUSAL_KINDS[error.serverMessage]
    : undefined

/** Whether an error is a service refusing a write because this is support-session. */
export const isReadOnlyRefusal = (error: unknown): boolean =>
  refusalKindOf(error) === 'read_only'

// Not `support_session_invalid` or `_unavailable`: a service without support-session
// configured, or an admin service that briefly cannot load its key, says so
// about a session that is still live.
const SESSION_OVER = new Set([
  'support_session_revoked',
  'support_session_expired',
])

/** Whether an error is a service refusing the session itself. */
export const isSessionOverRefusal = (error: unknown): boolean =>
  error instanceof HttpResponseError &&
  error.status === 401 &&
  error.serverMessage !== undefined &&
  SESSION_OVER.has(error.serverMessage)

/** How often an open support-session tab asks whether its session is still live. */
const SESSION_STATUS_INTERVAL_MS = 30 * 1000

/** How long the banner says a change was blocked. */
const REFUSAL_NOTICE_MS = 6000

/**
 * Tells the staff member when a request was refused, and why. The services
 * refuse every write in an observe session, an assist session's writes
 * outside its org, and a confined observe session's pages in the user's
 * other orgs; without this the screen would just not change, or show a
 * generic failure, and read as a bug in the app.
 * Mutations are the app's write path, so their cache is the one place to
 * listen; an unhandled rejection covers a write made outside one.
 */
const useRefusalNotice = (): SupportSessionRefusalKind | undefined => {
  const queryClient = useQueryClient()
  const [refused, setRefused] = useState<
    { kind: SupportSessionRefusalKind; at: number } | undefined
  >()

  useEffect(() => {
    const onError = (error: unknown) => {
      const kind = refusalKindOf(error)
      if (kind) {
        setRefused({ kind, at: Date.now() })
      }
    }
    // Queries as well as mutations: a read made with a POST is refused too,
    // and React Query keeps a query's error to itself.
    const unsubscribe = subscribeToCacheErrors(queryClient, onError)
    const onRejection = (event: PromiseRejectionEvent) => onError(event.reason)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      unsubscribe()
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [queryClient])

  useEffect(() => {
    if (refused === undefined) {
      return
    }
    const timer = window.setTimeout(
      () => setRefused(undefined),
      REFUSAL_NOTICE_MS,
    )
    return () => window.clearTimeout(timer)
  }, [refused])

  return refused?.kind
}

/**
 * Call `onError` with the error of every query and mutation in
 * `queryClient` that fails. Answers the unsubscribe.
 */
const subscribeToCacheErrors = (
  queryClient: QueryClient,
  onError: (error: unknown) => void,
): (() => void) => {
  const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'error') {
      onError(event.action.error)
    }
  })
  const unsubscribeMutations = queryClient
    .getMutationCache()
    .subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        onError(event.action.error)
      }
    })
  return () => {
    unsubscribeQueries()
    unsubscribeMutations()
  }
}

/**
 * The admin console's confirm page for another session with the same user,
 * opening where the staff member is now. There is no extending a session
 * from here: this tab holds no staff credential, and a new session is audited
 * on its own.
 */
export const extendUrl = (
  info: {
    mode?: string
    targetUserId: string
    reportId?: string
    orgId?: string
  },
  path: string,
): string =>
  `/admin/support-session?${new URLSearchParams(
    info.mode === 'assist' && info.orgId
      ? { userId: '', orgId: info.orgId, to: path }
      : {
          userId: info.targetUserId,
          ...(info.reportId && { reportId: info.reportId }),
          // The read-only session's org, which the console picks for it.
          ...(info.orgId && { org: info.orgId }),
          to: path,
        },
  ).toString()}`

const formatRemaining = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

/**
 * The banner inverts the page (text colour behind, background colour on
 * it), so its text holds AA contrast in both themes: the warning and accent
 * colours are mid-tones in the light theme, and neither white nor dark text
 * on them reaches 4.5:1. The mode's colour is the thick edge below it. The
 * design system's ghost and outline buttons, made for panels, take the
 * banner's foreground instead of their own.
 */
const ON_BANNER = 'text-bg hover:text-bg focus-visible:ring-bg'

/**
 * The watermark: fixed above the app for the whole session and not
 * dismissible, so no screenshot or screen share of a support-session tab can be
 * mistaken for the customer's own screen.
 */
export const SupportSessionBanner = () => {
  const supportSession = useSupportSession()
  const refused = useRefusalNotice()

  if (!supportSession) {
    return null
  }
  const { session, remainingMs, end } = supportSession
  const info = session.session
  const assist = info.mode === 'assist'

  return (
    <div
      // A labelled region, not a live one: the countdown changes every
      // second, and a live region would have a screen reader announce the
      // banner for the whole session. Only the refusal notice is live.
      role="region"
      aria-label="Support session"
      // A colour per mode, so a screenshot says which it was: read-only reads
      // as the customer, read-write changes things as DroidMap Support.
      className={`z-50 flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 bg-text text-bg px-4 py-2 text-sm border-b-4 ${
        assist ? 'border-accent' : 'border-warning'
      }`}
    >
      {assist ? (
        <WrenchScrewdriverIcon
          className="h-4 w-4 shrink-0 text-accent"
          aria-hidden
        />
      ) : (
        <EyeIcon className="h-4 w-4 shrink-0 text-warning" aria-hidden />
      )}
      <span>
        {assist ? (
          <>
            Read-write session · as DroidMap Support in{' '}
            <strong>{info.orgName ?? 'this organisation'}</strong> · changes are
            recorded against you ·{' '}
          </>
        ) : (
          <>
            Read-only session · viewing as <strong>{info.targetEmail}</strong>{' '}
            ·{' '}
          </>
        )}
        {/* Tabular digits in a fixed-width slot: the countdown ticks every
            second, and proportional digits (or 10:00 becoming 9:59) would
            shift everything after it on each tick. */}
        <strong className="inline-block min-w-[5ch] text-right tabular-nums">
          {formatRemaining(remainingMs)}
        </strong>{' '}
        left
      </span>
      <span className="opacity-90">You are {info.staffEmail}</span>
      {info.buildSha && info.buildSha !== BUILD_SHA && (
        <span className="opacity-90">
          Reported on build {info.buildSha}; you are on {BUILD_SHA}
        </span>
      )}
      {refused && (
        <span role="alert" className="font-semibold">
          {
            {
              read_only: 'That change was blocked: this session is read-only',
              wrong_org: assist
                ? `That change was blocked: this session works only in ${info.orgName ?? 'its organisation'}`
                : 'That page was blocked: this session covers only the organisation it was started in',
              not_allowed:
                'That change was blocked: a support session cannot make it',
            }[refused]
          }
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        <Button
          asChild
          size="xs"
          variant="ghost"
          className={cn(ON_BANNER, 'hover:bg-bg/10')}
        >
          <a
            href={extendUrl(
              info,
              `${window.location.pathname}${window.location.search}`,
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            Extend
          </a>
        </Button>
        <Button
          size="xs"
          variant="outline"
          className={cn(
            ON_BANNER,
            'border-bg/40 hover:border-bg/70 hover:bg-bg/10',
          )}
          onClick={() => void end()}
        >
          End session
        </Button>
      </div>
    </div>
  )
}
